<template>
  <div class="page-lead-profile">
    <bj-crud-form-modal
      ref="crudModal"
      not-crud
      model="Lead"
      endpoint-edit="admin/leads"
      :fields="editProfileFields"
      @saved="fetchLead()"
    />
    <bj-add-inquiry-modal
      ref="addInquiryModal"
      @added="reloadPage"
    />
    <div class="row gutter-md">
      <div class="col-md-4 lead-sidebar">
        <q-card class="full-height">
          <q-list
            class="scrollable-wrapper"
            separator
          >
            <q-collapsible
              opened
              label="Info"
              collapse-icon="keyboard_arrow_right"
              header-class="collapsible-header"
            >
              <q-card-separator />
              <q-card
                v-if="lead"
                square
                flat
              >
                <q-card-main>
                  <q-item class="col-9">
                    <q-item-main>
                      <q-item-tile label>
                        Created At
                      </q-item-tile>
                      <q-item-tile sublabel>
                        <SdDatetime
                          :value="lead.created_at"
                          :timezone="propertyTimezone"
                        />
                      </q-item-tile>
                    </q-item-main>
                  </q-item>
                  <q-item
                    v-if="lead.phone_number"
                    class="col-6"
                  >
                    <q-item-main>
                      <q-item-tile label>
                        Phone number
                      </q-item-tile>
                      <q-item-tile sublabel>
                        {{ lead.phone_number }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>
                  <q-item
                    v-if="lead.email"
                    class="col-6"
                  >
                    <q-item-main>
                      <q-item-tile label>
                        Email
                      </q-item-tile>
                      <q-item-tile sublabel>
                        {{ lead.email }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>
                  <q-item
                    v-if="lead.notes"
                    class="col-6"
                  >
                    <q-item-main>
                      <q-item-tile label>
                        Notes
                      </q-item-tile>
                      <q-item-tile
                        sublabel
                        class="lead-notes"
                      >
                        {{ lead.notes }}
                      </q-item-tile>
                    </q-item-main>
                  </q-item>
                  <q-item class="col-auto">
                    <q-btn
                      class="q-mr-sm"
                      outline
                      rounded
                      size="sm"
                      label="Edit"
                      color="black"
                      icon="edit"
                      @click.native="$refs.crudModal.open('edit', ...lead)"
                    />
                    <q-btn
                      outline
                      rounded
                      size="sm"
                      label="Add Inquiry"
                      color="green"
                      icon="add"
                      @click.native="openInquiryModal(lead)"
                    />
                  </q-item>
                  <q-inner-loading :visible="loading">
                    <q-spinner-puff
                      size="50px"
                      color="primary"
                    />
                    <div class="q-mt-md text-primary text-bold">
                      Updating...
                    </div>
                  </q-inner-loading>
                </q-card-main>
              </q-card>
            </q-collapsible>
            <q-collapsible
              v-if="inquiries"
              opened
              :label="'Inquiries: (' + inquiries.length + ')'"
              collapse-icon="keyboard_arrow_right"
              header-class="collapsible-header"
            >
              <q-card-separator />
              <q-card
                square
                flat
              >
                <q-card-main class="inquiries-list">
                  <div
                    v-for="(inquiry,index) in inquiries"
                    class="inquiry-item"
                  >
                    <div class="row text-left mb-2">
                      <div class="col-4">
                        Inquiry ID:
                      </div>
                      <div class="col-8">
                        <router-link :to="{ name: 'app.inquiries.timeline', params: { id: inquiry.id} }">
                          #{{ inquiry.id }}
                        </router-link>
                      </div>
                    </div>
                    <div class="row text-left mb-2">
                      <div class="col-4">
                        Property address:
                      </div>
                      <div class="col-8">
                        <router-link :to="{ name: 'app.properties.combined', params: { id: inquiry.property_id } }">
                          {{ inquiry.property_address }}
                        </router-link>
                        <q-chip
                          v-if="!inquiry.property_is_vacant"
                          dense
                          color="yellow"
                          text-color="black"
                          style="margin-left: 5px"
                        >
                          Occupied
                        </q-chip>
                      </div>
                    </div>
                    <div class="row text-left mb-2">
                      <div class="col-4">
                        Created at:
                      </div>
                      <div class="col-8">
                        <SdDatetime
                          :value="inquiry.created_at"
                          :timezone="inquiry.property_timezone"
                        />
                      </div>
                    </div>
                    <div
                      v-if="inquiry.persona_identification_url"
                      class="row text-left mb-2"
                    >
                      <div class="col-4">
                        Persona Inquiry:
                      </div>
                      <div class="col-8">
                        <a
                          :href="inquiry.persona_identification_url"
                          target="_blank"
                        >
                          Open
                        </a>
                      </div>
                    </div>
                    <div class="row text-left mb-2">
                      <div class="col-8">
                        <tour-status-chip
                          v-if="inquiry.tour_status"
                          :status="getHumanScheduleStatus(inquiry.tour_status, inquiry.is_taken_over)"
                        />
                        <q-chip
                          v-else
                          :color="inquiry.status === $config.inquiryStatuses.MATCHING_FAILED ? 'negative' : ''"
                          class="tour-status-badge"
                        >
                          {{ getHumanInquiryStatus(inquiry.status) }}
                        </q-chip>
                        <q-icon
                          v-if="inquiry.uninterested_at"
                          v-tippy
                          color="negative"
                          name="cancel"
                          title="Marked as uninterested"
                        />
                        <span>{{ checkPassGeolocation(inquiry) }}</span>
                      </div>
                      <div class="col-auto">
                        <q-btn
                          v-if="inquiry.status === $config.inquiryStatuses.PENDING || inquiry.status === $config.inquiryStatuses.MATCHING_FAILED"
                          v-tippy
                          size="sm"
                          rounded
                          outline
                          color="black"
                          icon="edit"
                          title="Edit"
                          class="q-mr-sm"
                          @click="openInquiryEditModal(inquiry)"
                        />
                        <q-btn
                          v-if="inquiry.status === $config.inquiryStatuses.PENDING && !inquiry.uninterested_at"
                          v-tippy
                          size="sm"
                          rounded
                          outline
                          color="negative"
                          icon="cancel"
                          title="Mark as uninterested"
                          class="q-mr-sm"
                          @click="markAsUninterested(inquiry)"
                        />
                      </div>
                    </div>
                    <q-card-separator
                      v-if="index !== inquiries.length -1"
                      class="mt-4 mb-4"
                    />
                  </div>
                </q-card-main>
              </q-card>
            </q-collapsible>
            <q-collapsible
              opened
              :label="'Identity History: (' + leadIdentityHistory.length + ')'"
              collapse-icon="keyboard_arrow_right"
              header-class="collapsible-header"
            >
              <q-card-separator />
              <q-card
                square
                flat
              >
                <q-card-main
                  v-if="leadIdentityHistory.length"
                >
                  <SdLeadIdentityHistoryTimeline
                    :identity-history="leadIdentityHistory"
                    :display-inquiry-link="true"
                  />
                </q-card-main>
              </q-card>
            </q-collapsible>
          </q-list>
        </q-card>
      </div>
      <div
        v-if="lead"
        class="col-md-8 col-xs-12"
      >
        <q-card
          id="timeline"
          ref="timeline"
          class="full-height"
        >
          <q-card-main class="relative-position scrollable-wrapper">
            <div
              v-for="(activityByDay, date, index) in activitiesByDays"
              v-if="!loading"
              :key="index"
            >
              <q-chip
                dense
                color="blue"
                text-color="white"
                class="event-date-chip"
              >
                {{ date }}
              </q-chip>
              <div
                v-for="activity in activityByDay"
                class="event-wrapper"
              >
                <p
                  v-if="activity.type === 'INQUIRY_CREATED'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} New inquiry at <b>{{ activity.property_address }}</b>
                </p>
                <p
                  v-if="activity.type === 'TOUR_SCHEDULED'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} Tour scheduled at <b>{{ activity.property_address }}</b>
                </p>
                <p
                  v-if="activity.type === 'TOUR_NO_SHOW'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} Tour resulted in no show at <b>{{
                    activity.property_address
                  }}</b>
                </p>
                <p
                  v-if="activity.type === 'TOUR_TECHNICAL_ISSUE'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} Tour resulted in technical issue at
                  <b>{{ activity.property_address }}</b>
                </p>
                <p
                  v-if="activity.type === 'TOUR_CANCELLED'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} Tour was cancelled at <b>{{ activity.property_address }}</b>
                  Reason: {{ getHumanScheduleStatus(activity.status, false) }}
                </p>
                <p
                  v-if="activity.type === 'TOUR_COMPLETED'"
                  class="text-center generic-event"
                >
                  {{ dateInHumanFormat(activity.date) }} Tour was completed at <b>{{ activity.property_address }}</b>.
                  <router-link
                    v-if="activity.survey_result_id"
                    :to="{ name: 'app.tours.survey-result', params: { id: activity.survey_result_id }}"
                  >
                    Go to review
                  </router-link>
                </p>
                <div
                  v-if="activity.type === 'OUTGOING_MESSAGE'"
                  class="outgoing-message-wrapper"
                >
                  <p style="color:#59B380">
                    {{ activity.causer }}
                  </p>
                  <p>{{ activity.message }}</p>
                  <span>{{ dateInHumanFormat(activity.date) }} </span>
                </div>
                <div
                  v-if="activity.type === 'INCOMING_MESSAGE'"
                  class="incoming-message-wrapper"
                >
                  <p style="color:#41A5F5">
                    {{ lead.full_name }}
                  </p>
                  <p>{{ activity.message }}</p>
                  <span>{{ dateInHumanFormat(activity.date) }} </span>
                </div>
                <div
                  v-if="activity.type === 'DISQUALIFIED'"
                  class="disqualified-wrapper"
                >
                  <p>
                    Lead disqualified
                    <small>
                      (
                      <router-link :to="{ name: 'app.inquiries.timeline', params: { id: activity.inquiry_id} }">
                        #{{ activity.inquiry_id }}
                      </router-link>
                      - {{ activity.property_address }})
                    </small>
                  </p>
                  <div v-html="activity.message" />
                  <span>{{ dateInHumanFormat(activity.date) }}</span>
                </div>
              </div>
            </div>
          </q-card-main>
          <q-card-separator />
          <q-card-main class="row gutter-xs">
            <div class="col-11">
              <q-field>
                <q-input
                  v-model="textMessageToLead"
                  inverted
                  placeholder="Type a message to the lead"
                  @keyup.enter.native="sendTextToLead()"
                />
              </q-field>
            </div>
            <div class="col-1">
              <q-btn
                :disabled="textMessageToLead === '' || smsIsSending"
                rounded
                size="md"
                label="Send"
                no-caps
                @click.prevent="sendTextToLead()"
              />
            </div>
          </q-card-main>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash.get';
import TourStatusChip from '../../../components/tour-status-chip';
import BjAddInquiryModal from '../../../components/add-inquiry-modal';
import { getHumanInquiryStatus } from '../../../utils/InquiryUtil';
import { getHumanScheduleStatus } from '../../../utils/ScheduleUtil';
import SdLeadIdentityHistoryTimeline from '../../../components/common/SdLeadIdentityHistoryTimeline';
import { handleAccessAttemptsPass } from '../../../utils/AccessAttemptsUtil';

export default {
  components: {
    BjAddInquiryModal,
    TourStatusChip,
    SdLeadIdentityHistoryTimeline,
  },
  data() {
    return {
      lead: null,
      inquiries: null,
      activitiesByDays: [],
      loading: null,
      activitiesFetchInterval: null,
      textMessageToLead: '',
      propertyTimezone: null,
      smsIsSending: false,
      leadIdentityHistory: [],
      queryParams: {
        id: this.$route.params.userId ? [this.$route.params.userId] : null,
      },
      editProfileFields: [
        {
          name: 'first_name',
          label: 'First Name',
          type: 'text',
          rules: ['required'],
          class: 'col-md-6 col-xs-12',
        },
        {
          name: 'last_name',
          label: 'Last Name',
          type: 'text',
          class: 'col-md-6 col-xs-12',
        },
        {
          name: 'phone_number',
          label: 'Phone Number',
          type: 'text',
          rules: [{
            name: 'requiredUnless',
            field: 'email',
          }, 'phone'],
          error: 'You must fill a valid phone number',
          class: 'col-md-6 col-xs-12',
        },
        {
          name: 'email',
          label: 'Email',
          type: 'email',
          rules: [{
            name: 'requiredUnless',
            field: 'phone_number',
          }, 'email'],
          error: 'You must fill a valid email address',
          class: 'col-md-6 col-xs-12',
        },
        {
          name: 'notes',
          label: 'Notes',
          type: 'textarea',
          class: 'col-md-12 col-xs-12',
        },
        {
          name: 'is_blocked',
          label: 'Block lead',
          type: 'checkbox',
          class: 'col-md-6 col-xs-12',
        },
      ],
    };
  },
  mounted() {
    this.uiLoading();
    this.init();
  },
  beforeDestroy() {
    window.clearInterval(this.activitiesFetchInterval);
  },
  methods: {
    getHumanInquiryStatus,
    getHumanScheduleStatus,
    async init() {
      await this.fetchLead();
      await this.fetchInquiries();
      await this.fetchActivities();
      this.startActivitiesFetchInterval();
      this.uiLoading(false);
    },
    async fetchLead() {
      this.lead = await this.api().request('GET', `admin/leads/${this.$route.params.id}`);
      this.leadIdentityHistory = this.lead.identity_history === null ? [] : this.lead.identity_history;
      this.lead.is_blocked = !!this.lead.is_blocked;
      const leadBlockedIcon = this.lead.is_blocked ? '<i class="ml-1 text-red q-icon fa fa-ban"/>' : '';
      const pageHeaderTitle = `Lead profile - ${this.lead.full_name} ${leadBlockedIcon}`;
      this.$root.$emit('update-page-header-title', pageHeaderTitle);
    },
    async fetchInquiries() {
      this.inquiries = await this.api().request('GET', `admin/leads/${this.$route.params.id}/inquiries`);
      this.propertyTimezone = this.inquiries.length ? this.inquiries[0].property_timezone : null;
    },
    async fetchActivities() {
      this.loading = true;
      const activities = await this.api().request('GET', `admin/leads/${this.$route.params.id}/activities`);
      this.activitiesByDays = this.sortAndGroupActivitiesByDay(activities);
      this.loading = false;
      this.$nextTick(() => {
        const timelineEl = this.$refs.timeline.$el.querySelector('.scrollable-wrapper');
        timelineEl.scrollTop = timelineEl.scrollHeight;
      });
    },
    startActivitiesFetchInterval() {
      if (this.activitiesFetchInterval) {
        return;
      }

      this.activitiesFetchInterval = window.setInterval(() => {
        this.fetchActivities();
      }, 60000);
    },
    checkPassGeolocation(inquiry) {
      return handleAccessAttemptsPass(get(inquiry, 'access_attempts', false));
    },
    sortAndGroupActivitiesByDay(activities) {
      const groupedByDay = {};
      const sortedActivities = activities.sort((a, b) => (this.$momentUtil(a.date, this.propertyTimezone).isSameOrAfter(this.$momentUtil(b.date, this.propertyTimezone)) ? 1 : -1));
      sortedActivities.forEach((activity) => {
        const dayKey = this.$momentUtil(activity.date, this.propertyTimezone).format('MM/DD/YY');
        if (groupedByDay[dayKey]) {
          groupedByDay[dayKey].push(activity);
        } else {
          groupedByDay[dayKey] = [activity];
        }
      });
      return groupedByDay;
    },
    dateInHumanFormat(date) {
      return this.$momentUtil(date, this.propertyTimezone).toDisplayShortTimeFormat();
    },
    openInquiryModal(lead) {
      const leadDetails = {
        first_name: lead.first_name,
        last_name: lead.last_name,
        phone_number: lead.phone_number,
        email: lead.email,
      };
      this.$refs.addInquiryModal.show(leadDetails);
    },
    openInquiryEditModal(inquiry) {
      this.$refs.addInquiryModal.show({
        id: inquiry.id,
        property_id: inquiry.property_id,
        property_name: inquiry.property_address,
        property: {
          ...inquiry.property,
          address: inquiry.property_address,
        },
        unit_id: inquiry.unit_id,
        unit_name: inquiry.unit_id !== null && inquiry.unit_name || '',
        comments: inquiry.comments,
        inquiry_source_id: inquiry.inquiry_source_id,
      });
    },
    reloadPage() {
      window.location.reload();
    },
    markAsUninterested(inquiry) {
      this.uiConfirm('Are you sure you want to mark this inquiry as "uninterested"? No further reminders and messages will be sent to the lead')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            is_uninterested: true,
          }).then(() => {
            this.fetchInquiries();
            this.uiNotify('Inquiry marked as uninterested');
          });
        });
    },
    sendTextToLead() {
      if (this.textMessageToLead !== '' && this.smsIsSending === false) {
        this.smsIsSending = true;
        this.api().request('POST', `admin/leads/${this.lead.id}/notifications`, {
          message: this.textMessageToLead,
          channel: 'sms',
        })
          .then(() => {
            this.textMessageToLead = '';
            this.fetchActivities();
          }).catch((error) => {
            this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
          })
          .finally(() => {
            this.smsIsSending = false;
          });
      }
    },
  },
};
</script>

<style lang="scss">
.page-lead-profile {
  .q-btn-round {
    height: auto;
    width: auto;
  }

  .q-table-container {
    box-shadow: none;
  }

  .scrollable-wrapper {
    overflow-y: scroll;
  }

  .event-date-chip {
    margin: 0.5rem;
    padding: 0.25rem 0.5rem;
    position: relative;
    left: 45%;
  }

  .lead-sidebar {

    .q-collapsible-inner {
      margin-bottom: 16px;
    }

    .q-list {
      max-height: 100vh;
      padding: 8px 8px 8px 16px;
    }

    .q-item-label {
      padding-left: 8px;
    }

    .q-item {
      padding-left: 0;
      padding-right: 0;
    }

    .q-collapsible-sub-item {
      padding-left: 0;
      padding-right: 8px;
    }

    .q-collapsible + .q-collapsible {
      padding-top: 8px;
    }

    .collapsible-header {
      font-weight: bold
    }

    .inquiries-list {
      padding-top: 21px;

      .inquiry-item .q-card-separator {
        margin-left: -16px;
        margin-right: -16px;
      }
    }

    .lead-notes{
      max-height: 150px;
      overflow-y: auto;
    }
  }

  #timeline {
    padding: 0.5rem;

    .q-card-main:first-child {
      height: 66vh;
    }

    .event-wrapper {
      display: flex;
      flex-direction: column;
      margin: 0.75rem 0;

      .generic-event {
        margin: 0;
        font-size: 14px;
        line-height: 24px;
        text-align: center;
        color: #474F55;
      }

      .outgoing-message-wrapper {
        background: #F0F0F0;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        padding: 0.5rem 1rem;
        width: 60%;

        p:first-child {
          margin-bottom: 0.25rem;
        }

        p:nth-child(2) {
          width: 90%;
        }

        p {
          margin-bottom: 0;
          font-size: 14px;
        }

        span {
          align-self: flex-end;
        }
      }

      .incoming-message-wrapper {
        background: #eef6fd;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-self: flex-end;
        padding: 0.5rem 1rem;
        width: 60%;
        margin-bottom: 1rem;

        p:first-child {
          margin-bottom: 0.25rem;
        }

        p {
          margin-bottom: 0;
          font-size: 14px;
        }

        span {
          align-self: flex-end;
        }
      }

      .disqualified-wrapper {
        background: #FBDFDB;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-self: flex-end;
        padding: 0.5rem 1rem;
        width: 60%;
        margin-bottom: 1rem;

        p {
          margin-bottom: 0.25rem;
        }

        ul {
          margin: 0;
          padding-inline-start: 1rem;
          margin-bottom: 0.25rem;

          strong {
            font-size: 100%;
          }
        }

        span {
          align-self: flex-end;
        }
      }
    }
  }

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #DFE5EB;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #cce6eb;
  }
}
</style>
